/**
 * Timezone Utilities — SAP T001P-aligned
 *
 * All time displays use the employee's resolved timezone (Employee -> Location -> Tenant fallback).
 * The resolved timezone is persisted to localStorage during login by useAuth hook.
 *
 * Usage:
 *   import { formatTimeInTenantTz, nowInTenantTz, todayInTenantTz } from '@/lib/timezone';
 */

import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

const TIMEZONE_KEY = 'tenant_timezone';
const DEFAULT_TIMEZONE = 'Asia/Kolkata';
const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Get the resolved timezone from localStorage (set during login).
 * Falls back to Asia/Kolkata (project default per Tenant model).
 */
export function getTenantTimezone(): string {
  return localStorage.getItem(TIMEZONE_KEY) ?? DEFAULT_TIMEZONE;
}

/**
 * Set the resolved timezone in localStorage.
 * Called during login after the backend resolves Employee -> Location -> Tenant chain.
 */
export function setTenantTimezone(timezone: string): void {
  localStorage.setItem(TIMEZONE_KEY, timezone);
}

/**
 * Clear timezone on logout.
 */
export function clearTenantTimezone(): void {
  localStorage.removeItem(TIMEZONE_KEY);
}

/**
 * Format a UTC timestamp in tenant timezone.
 * Use for: check-in/out times, approval timestamps, event times.
 */
export function formatTimeInTenantTz(utcDate: string | Date, formatStr = 'hh:mm a'): string {
  if (!utcDate) return '-';
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(utcDate), tz, formatStr);
}

/**
 * Format a UTC timestamp as date+time in tenant timezone.
 */
export function formatDateTimeInTenantTz(
  utcDate: string | Date,
  formatStr = 'MMM d, yyyy hh:mm a',
): string {
  if (!utcDate) return '-';
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(utcDate), tz, formatStr);
}

/**
 * Get current date/time in tenant timezone (for live clock).
 * Returns a Date object adjusted to represent the tenant's local time.
 */
export function nowInTenantTz(): Date {
  const tz = getTenantTimezone();
  return toZonedTime(new Date(), tz);
}

/**
 * Get "today" date string (YYYY-MM-DD) in tenant timezone.
 */
export function todayInTenantTz(): string {
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
}

/**
 * Format a UTC date as date-only in tenant timezone.
 * Use for: display of dates in tables, detail pages, badges.
 */
export function formatDateInTenantTz(utcDate: string | Date, formatStr = 'MMM d, yyyy'): string {
  if (!utcDate) return '-';
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(utcDate), tz, formatStr);
}

/**
 * Convert a UTC date to YYYY-MM-DD string for HTML <input type="date"> value.
 * Uses tenant timezone to avoid off-by-one day shifts near midnight.
 */
export function formatDateForInput(utcDate: string | Date): string {
  if (!utcDate) return '';
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(utcDate), tz, 'yyyy-MM-dd');
}

/**
 * Convert a UTC datetime to YYYY-MM-DDTHH:mm string for HTML <input type="datetime-local"> value.
 * Uses tenant timezone.
 */
export function formatDateTimeForInput(utcDate: string | Date): string {
  if (!utcDate) return '';
  const tz = getTenantTimezone();
  return formatInTimeZone(new Date(utcDate), tz, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Convert a naive datetime string from <input type="datetime-local"> to UTC ISO string.
 * The input is in tenant timezone (user's local time); this converts to UTC for API submission.
 * Use for: reminderAt, dueDate+dueTime, any datetime-local input → API payload.
 *
 * Example: "2026-03-25T00:40" in IST → "2026-03-24T19:10:00.000Z" (UTC)
 */
export function localDateTimeToUtc(naiveDatetime: string, timezone = getTenantTimezone()): string {
  if (!naiveDatetime) return '';
  if (!LOCAL_DATE_TIME_PATTERN.test(naiveDatetime)) {
    throw new RangeError('Date and time must use YYYY-MM-DDTHH:mm');
  }

  const structuralDate = new Date(`${naiveDatetime}:00.000Z`);
  if (
    Number.isNaN(structuralDate.getTime()) ||
    structuralDate.toISOString().slice(0, 16) !== naiveDatetime
  ) {
    throw new RangeError('Date and time is invalid');
  }

  const utcDate = fromZonedTime(`${naiveDatetime}:00`, timezone);
  if (Number.isNaN(utcDate.getTime())) {
    throw new RangeError(`Date and time is invalid in ${timezone}`);
  }

  const roundTrip = formatInTimeZone(utcDate, timezone, "yyyy-MM-dd'T'HH:mm");
  if (roundTrip !== naiveDatetime) {
    throw new RangeError(`Date and time does not exist in ${timezone}`);
  }

  return utcDate.toISOString();
}

/**
 * Describe the UTC offset that applies to a selected tenant-local date/time.
 */
export function formatTenantDateTimeOffset(
  naiveDatetime: string,
  timezone = getTenantTimezone(),
): string {
  const utcDate = localDateTimeToUtc(naiveDatetime, timezone);
  return formatInTimeZone(utcDate, timezone, 'XXX');
}

/**
 * Get start of week (Monday-based) in tenant timezone.
 */
export function weekStartInTenantTz(): Date {
  const now = nowInTenantTz();
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1; // Sunday(0)->6, Mon(1)->0, Tue(2)->1, etc.
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - diff);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
}
